/**
 * HOOK: useSmartDefaults
 *
 * Applies smart defaults for empty fields when asistidoMode is active.
 * Runs via useEffect when asistidoMode && rows.length > 0.
 * Each default is logged as a mutations-compatible log entry.
 *
 * Signature: useSmartDefaults(rows, asistidoMode, updateRowField, fechaCese)
 *
 * Returns: { appliedDefaults, defaultsApplied }
 */

import { useState, useEffect } from 'react'

/* ------------------------------------------------------------------ */
/*  Internal helpers                                                    */
/* ------------------------------------------------------------------ */

function generateId() {
  return `sd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/* ------------------------------------------------------------------ */
/*  Pure: computeRowChanges — exported for testing                      */
/* ------------------------------------------------------------------ */

/**
 * Computes default changes for a single row. Pure function — no
 * side effects, no React dependencies.
 *
 * @param {object|null} row
 * @param {string}      fechaCese — date of cese (YYYY-MM-DD)
 * @returns {Array<{ field, from, to, reasonCode, reasonDetail }>}
 */
export function computeRowChanges(row, fechaCese) {
  if (!row) return []

  const changes = []

  // Rule 1: Minimum monotributo category
  // If categoriaMonotributo is empty and not already 'A', apply minimum.
  if (!row.categoriaMonotributo) {
    changes.push({
      field: 'categoriaMonotributo',
      from: row.categoriaMonotributo,
      to: 'A',
      reasonCode: 'smart_default',
      reasonDetail: 'Default aplicado: categoría monotributo → A',
    })
  }

  // Rule 2: EXCLUIR_DIVISOR for zero/empty monto
  // If monto is 0, empty string, or null/undefined, set estrategia.
  // Skip if already EXCLUIR_DIVISOR (idempotent — prevents duplicate log entries).
  if (
    (row.monto === 0 || row.monto === '' || row.monto == null) &&
    row.estrategia !== 'EXCLUIR_DIVISOR'
  ) {
    changes.push({
      field: 'estrategia',
      from: row.estrategia,
      to: 'EXCLUIR_DIVISOR',
      reasonCode: 'smart_default',
      reasonDetail: 'Default aplicado: estrategia → EXCLUIR_DIVISOR (monto cero o vacío)',
    })
  }

  // Rule 3: Periodo inference (not committed — shown as suggestion)
  // When no periodo and fechaCese exists, the value is shown as a
  // placeholder but NOT committed to the row. This function only
  // returns committed changes, so periodo is intentionally excluded.

  return changes
}

/* ------------------------------------------------------------------ */
/*  Hook                                                                */
/* ------------------------------------------------------------------ */

export default function useSmartDefaults(
  rows,
  asistidoMode,
  updateRowField,
  fechaCese,
) {
  const [appliedDefaults, setAppliedDefaults] = useState([])

  useEffect(() => {
    // Guard: only apply defaults when asistido mode is active and rows exist
    if (!asistidoMode || !rows || rows.length === 0) {
      return
    }

    const newDefaults = []

    for (const row of rows) {
      const changes = computeRowChanges(row, fechaCese)

      for (const change of changes) {
        // Resolve the row identifier — rows may use _rowId, rowId, or id
        const rowId = row._rowId ?? row.rowId ?? row.id

        // Apply the default value via the provided update function
        if (rowId != null) {
          updateRowField(rowId, change.field, change.to)
        }

        // Build a full mutationsLog-compatible entry
        newDefaults.push({
          id: generateId(),
          periodo: row.periodo || '',
          field: change.field,
          originalValue: change.from,
          newValue: change.to,
          reasonCode: change.reasonCode,
          reasonDetail: change.reasonDetail,
          timestamp: new Date().toISOString(),
        })
      }
    }

    // Only update state if new defaults were actually produced
    if (newDefaults.length > 0) {
      setAppliedDefaults((prev) => [...prev, ...newDefaults])
    }
  }, [asistidoMode, rows, updateRowField, fechaCese])

  return {
    appliedDefaults,
    defaultsApplied: appliedDefaults.length > 0,
  }
}
